import React from 'react';
import { healthAPI } from '../api/healthAPI';
import axiosApi from '../api/daonAxios';
import CODE from '../constants/code';

class GlobalStepUpdater {
  constructor() {
    this.isUpdating = false;
    this.currentSteps = 0;
    this.listeners = new Set();
    this.permissionChecked = false;
    this.hasPermission = false;

    // 날짜 관련
    this.lastDate = new Date().toDateString();
    this.LAST_DATE_KEY = 'stepUpdater_lastDate';
    this.STORAGE_KEY = 'healthPermissionGranted';

    // Android 기준점 관련
    this.androidSteps = 0;
    this.deviceBaseSteps = 0;
    this.serverBaseSteps = 0;
    this.isBaseStepsSet = false;
    this.initializationComplete = false;

    // 환경 감지
    const userAgent = navigator.userAgent || '';
    this.isAndroid = /Android/i.test(userAgent) || userAgent.includes('wv');
    this.isIOS = /iPad|iPhone|iPod/.test(userAgent);
    this.isReactNativeWebView = !!window.ReactNativeWebView;

    // 메시지 리스너 등록
    this.setupMessageListener();
    this.loadStoredPermission();
    this.loadStoredBaseSteps();
  }

  getUserStorageKey = (baseKey) => {
    try {
      const loginUser = sessionStorage.getItem("loginUser");
      if (loginUser) {
        const user = JSON.parse(loginUser);
        return `${baseKey}_${user.usrInfoId}`;
      }
    } catch (error) {
      console.error('사용자 정보 파싱 실패:', error);
    }
    return baseKey;
  };

  getUserKeyWithDate = (key) => {
    try {
      const loginUser = sessionStorage.getItem("loginUser");
      const today = new Date();
      const todayStr = today.getFullYear() +
        String(today.getMonth() + 1).padStart(2, '0') +
        String(today.getDate()).padStart(2, '0');
      if (loginUser) {
        const user = JSON.parse(loginUser);
        return `${key}_${user.usrInfoId}_${todayStr}`;
      }
      return `${key}_${todayStr}`;
    } catch (error) {
      const today = new Date();
      const todayStr = today.getFullYear() +
        String(today.getMonth() + 1).padStart(2, '0') +
        String(today.getDate()).padStart(2, '0');
      return `${key}_${todayStr}`;
    }
  };

  // Android 센서값 받는 리스너
  // GlobalStepUpdater.js - setupMessageListener()
  setupMessageListener() {
    window.addEventListener('message', (event) => {
      try {
        const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;

        // ★ Native에서 온 MIDNIGHT_RESET 직접 처리
        if (data.type === 'MIDNIGHT_RESET') {
          console.log('자정 리셋 이벤트 수신 (Native)', data.newDate);
          this.deviceBaseSteps = 0;
          this.serverBaseSteps = 0;
          this.androidSteps = 0;
          this.currentSteps = 0;
          this.isBaseStepsSet = false;
          this.initializationComplete = false;

          this.clearYesterdayData();

          const dateKey = this.getUserStorageKey(this.LAST_DATE_KEY);
          localStorage.setItem(dateKey, new Date().toDateString());
          this.lastDate = new Date().toDateString();

          this.notifyListeners({
            success: true,
            steps: 0,
            triggerType: 'MIDNIGHT_RESET',
            dateChanged: true,
            resetType: 'native_midnight'
          });
          return;
        }

        if (data.type === 'STEP_CURRENT_STEPS' || data.type === 'STEP_UPDATE') {
          const steps = data.steps || data.data?.steps || 0;
          this.androidSteps = steps;

          // 날짜 변경 체크 (센서값 받을 때마다)
          const dateChanged = this.checkDateChangeOnly();

          if (dateChanged) {
            // 날짜 바뀌었으면 현재 센서값을 새 기준점으로
            this.deviceBaseSteps = steps;
            this.serverBaseSteps = 0;
            this.isBaseStepsSet = true;
            this.initializationComplete = true;
            this.saveBaseSteps();
            console.log(`자정 리셋 - 새 기준점: ${steps}`);

            this.notifyListeners({
              success: true,
              steps: 0,
              triggerType: 'MIDNIGHT_RESET',
              dateChanged: true
            });
          } else if (!this.isBaseStepsSet && steps > 0 && this.hasPermission) {
            // 기준점 설정 안 됐으면 초기화 실행
            this.initializeBaseSteps(steps);
          }
        }

        // 권한 상태 업데이트
        if (data.type === 'STEP_COUNTER_PERMISSION_STATUS') {
          if (data.granted || data.status === 'PERMISSION_GRANTED') {
            this.hasPermission = true;
            this.permissionChecked = true;
            this.savePermissionToStorage(true);
          }
        }
      } catch (e) { }
    });
  }

  // 날짜 변경만 체크 (사이드이펙트 없이)
  checkDateChangeOnly() {
    const currentDate = new Date().toDateString();
    const dateKey = this.getUserStorageKey(this.LAST_DATE_KEY);
    const storedDate = localStorage.getItem(dateKey);

    if (storedDate && storedDate !== currentDate) {
      // 날짜 업데이트
      localStorage.setItem(dateKey, currentDate);
      this.lastDate = currentDate;

      // 어제 기준점 데이터 삭제
      this.clearYesterdayData();

      return true;
    }

    if (!storedDate) {
      localStorage.setItem(dateKey, currentDate);
      this.lastDate = currentDate;
    }

    return false;
  }

  // 어제 날짜 localStorage 정리
  clearYesterdayData() {
    try {
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      const yesterdayStr = yesterday.getFullYear() +
        String(yesterday.getMonth() + 1).padStart(2, '0') +
        String(yesterday.getDate()).padStart(2, '0');

      const loginUser = sessionStorage.getItem("loginUser");
      if (loginUser) {
        const user = JSON.parse(loginUser);
        const keys = ['stepsDashboard_baseStepsSet', 'stepsDashboard_serverBase', 'stepsDashboard_deviceBase', 'stepsDashboard_initialized'];
        keys.forEach(key => {
          localStorage.removeItem(`${key}_${user.usrInfoId}_${yesterdayStr}`);
        });
      }

      localStorage.removeItem('lastKnownSteps_iOS');
      localStorage.removeItem('lastKnownSteps_Total');
      localStorage.removeItem('lastKnownSteps_Date');

      console.log('✅ 어제 데이터 정리 완료');
    } catch (error) {
      console.error('어제 데이터 정리 실패:', error);
    }
  }

  // 저장된 기준점 로드
  loadStoredBaseSteps() {
    try {
      const savedBaseStepsSet = localStorage.getItem(this.getUserKeyWithDate('stepsDashboard_baseStepsSet'));
      const savedServerBase = localStorage.getItem(this.getUserKeyWithDate('stepsDashboard_serverBase'));
      const savedDeviceBase = localStorage.getItem(this.getUserKeyWithDate('stepsDashboard_deviceBase'));

      if (savedBaseStepsSet === 'true' && savedServerBase && savedDeviceBase) {
        this.serverBaseSteps = parseInt(savedServerBase);
        this.deviceBaseSteps = parseInt(savedDeviceBase);
        this.isBaseStepsSet = true;
        this.initializationComplete = true;
        console.log('✅ 저장된 기준점 복원:', { server: this.serverBaseSteps, device: this.deviceBaseSteps });
      }
    } catch (error) {
      console.error('기준점 로드 실패:', error);
    }
  }

  // 기준점 저장
  saveBaseSteps() {
    try {
      localStorage.setItem(this.getUserKeyWithDate('stepsDashboard_baseStepsSet'), 'true');
      localStorage.setItem(this.getUserKeyWithDate('stepsDashboard_serverBase'), this.serverBaseSteps.toString());
      localStorage.setItem(this.getUserKeyWithDate('stepsDashboard_deviceBase'), this.deviceBaseSteps.toString());
      localStorage.setItem(this.getUserKeyWithDate('stepsDashboard_initialized'), 'true');
    } catch (error) {
      console.error('기준점 저장 실패:', error);
    }
  }

  // StepsDashboard랑 동일한 초기화 로직
  async initializeBaseSteps(deviceSteps) {
    if (this.isBaseStepsSet) return;

    console.log('🔄 기준점 초기화 시작...', { deviceSteps });

    try {
      const serverSteps = await Promise.race([
        this.getTodayStepsFromServer(),
        new Promise((_, reject) => setTimeout(() => reject(new Error('타임아웃')), 5000))
      ]);

      if (serverSteps > 0) {
        this.serverBaseSteps = 0;
        this.deviceBaseSteps = Math.max(0, deviceSteps - serverSteps);
      } else {
        this.serverBaseSteps = 0;
        this.deviceBaseSteps = deviceSteps;
      }

      this.isBaseStepsSet = true;
      this.initializationComplete = true;
      this.saveBaseSteps();

      console.log('✅ 기준점 설정 완료:', {
        serverSteps,
        deviceSteps,
        deviceBase: this.deviceBaseSteps
      });

    } catch (error) {
      console.error('기준점 초기화 실패:', error);
      this.serverBaseSteps = 0;
      this.deviceBaseSteps = deviceSteps;
      this.isBaseStepsSet = true;
      this.initializationComplete = true;
      this.saveBaseSteps();
    }
  }

  // 계산된 최종 걸음수
  getCalculatedSteps() {
    if (this.isAndroid && this.hasPermission && this.isBaseStepsSet) {
      const calculated = Math.max(
        0,
        this.serverBaseSteps + Math.max(0, this.androidSteps - this.deviceBaseSteps)
      );
      return calculated;
    } else if (this.isIOS && this.hasPermission) {
      return Math.max(0, this.currentSteps || 0);
    }
    return 0;
  }

  // Android에 현재 걸음수 요청
  requestAndroidSteps() {
    if (window.ReactNativeWebView && this.isAndroid) {
      window.ReactNativeWebView.postMessage(JSON.stringify({
        type: 'STEP_GET_CURRENT',
        payload: {}
      }));
    }
  }

  // 기존 checkDateChange (호환용)
  checkDateChange() {
    const changed = this.checkDateChangeOnly();
    if (changed && this.isAndroid) {
      // 센서값 요청해서 새 기준점 설정하도록 유도
      this.requestAndroidSteps();
    }
    return changed;
  }

  resetDailyData() {
    console.log('🔄 자정 리셋: 일일 데이터 초기화');

    this.currentSteps = 0;
    this.serverBaseSteps = 0;
    this.isBaseStepsSet = false;
    this.initializationComplete = false;

    this.clearYesterdayData();

    if (window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage(JSON.stringify({
        type: 'STEP_MIDNIGHT_RESET',
        message: '자정 리셋',
        timestamp: Date.now()
      }));
    }

    this.notifyListeners({
      success: true,
      steps: 0,
      triggerType: 'MIDNIGHT_RESET',
      dateChanged: true,
      resetType: 'daily'
    });
  }

  async loadStoredPermission() {
    try {
      if (!window.ReactNativeWebView) return false;

      this.checkDateChange();

      const permissionKey = this.getUserStorageKey(this.STORAGE_KEY);
      const storedPermission = localStorage.getItem(permissionKey);
      if (storedPermission === 'true') {
        this.permissionChecked = true;
        this.hasPermission = true;
      }

      const restoreResult = await this.restoreFromServerOnFreshInstall();
      if (restoreResult.restored) {
        console.log(`✅ 서버에서 ${restoreResult.steps}걸음 복원 완료`);
      }
    } catch (error) {
      console.error('권한 로드 실패:', error);
    }
  }

  savePermissionToStorage(hasPermission) {
    try {
      const key = this.getUserStorageKey(this.STORAGE_KEY);
      localStorage.setItem(key, hasPermission.toString());
    } catch (error) {
      console.error('권한 저장 실패:', error);
    }
  }

  async checkPermission() {
    if (this.permissionChecked && this.hasPermission) {
      return this.hasPermission;
    }

    if (this.permissionChecked && !this.hasPermission) {
      return this.hasPermission;
    }

    try {
      if (!window.ReactNativeWebView) {
        this.permissionChecked = true;
        this.hasPermission = false;
        this.savePermissionToStorage(false);
        return false;
      }

      if (window.HealthKitObserver) {
        const permissionStatus = await window.HealthKitObserver.checkPermissionStatus();
        this.hasPermission = permissionStatus.isAuthorized;
      } else {
        try {
          const permissionStatus = await healthAPI.checkPermission();
          this.hasPermission = permissionStatus.permissionGranted;
        } catch (error) {
          this.hasPermission = false;
        }
      }

      this.permissionChecked = true;
      this.savePermissionToStorage(this.hasPermission);
      return this.hasPermission;

    } catch (error) {
      console.error('권한 확인 오류:', error);
      this.permissionChecked = true;
      this.hasPermission = false;
      this.savePermissionToStorage(false);
      return false;
    }
  }

  async updateSteps(triggerType = 'MANUAL') {
    if (this.isUpdating) return { success: false, reason: 'already_updating' };

    this.checkDateChange();
    this.isUpdating = true;

    try {
      const hasPermission = await this.checkPermission();

      if (!hasPermission) {
        return { success: false, reason: 'no_permission' };
      }

      // Android: 센서값 요청 후 대기
      if (this.isAndroid) {
        this.requestAndroidSteps();
        await new Promise(resolve => setTimeout(resolve, 1500));

        // 기준점 아직 안 됐으면 초기화
        if (!this.isBaseStepsSet && this.androidSteps > 0) {
          await this.initializeBaseSteps(this.androidSteps);
        }
      }

      // iOS: HealthKit에서 걸음수 가져오기
      if (this.isIOS) {
        try {
          const healthKitData = await Promise.race([
            healthAPI.getStepCount(),
            new Promise((_, reject) => setTimeout(() => reject(new Error('타임아웃')), 3000))
          ]);
          this.currentSteps = healthKitData?.value || 0;
        } catch (error) {
          console.error('HealthKit 조회 실패:', error);
        }
      }

      // 최종 걸음수 계산
      const steps = this.getCalculatedSteps();
      this.currentSteps = steps;

      // 서버 전송
      if (steps > 0) {
        await this.sendToServer(steps, triggerType);
      }

      this.notifyListeners({ success: true, steps, triggerType });
      return { success: true, steps };

    } catch (error) {
      console.error(`❌ 업데이트 실패:`, error.message);
      this.notifyListeners({ success: false, error: error.message });
      return { success: false, error: error.message };
    } finally {
      this.isUpdating = false;
    }
  }

  async getSteps() {
    if (!window.ReactNativeWebView) {
      return 0;
    }
    return this.getCalculatedSteps();
  }

  async restoreFromServerOnFreshInstall() {
    const APP_INSTALL_KEY = 'app_installed_timestamp';
    const LAST_SERVER_SYNC_KEY = 'last_server_sync_date';

    try {
      const installTime = localStorage.getItem(APP_INSTALL_KEY);
      const today = new Date().toDateString();

      if (!installTime) {
        localStorage.setItem(APP_INSTALL_KEY, Date.now().toString());
        const serverSteps = await this.getTodayStepsFromServer();
        if (serverSteps > 0) {
          this.currentSteps = serverSteps;
          this.notifyListeners({
            success: true,
            steps: serverSteps,
            triggerType: 'RESTORE_FROM_SERVER',
            isRestored: true
          });
          return { restored: true, steps: serverSteps };
        }
      }

      const lastSyncDate = localStorage.getItem(LAST_SERVER_SYNC_KEY);
      if (lastSyncDate !== today) {
        localStorage.setItem(LAST_SERVER_SYNC_KEY, today);
        const serverSteps = await this.getTodayStepsFromServer();
        if (serverSteps > 0) {
          this.currentSteps = Math.max(this.currentSteps, serverSteps);
          this.notifyListeners({
            success: true,
            steps: this.currentSteps,
            triggerType: 'DAILY_SYNC'
          });
        }
      }

      return { restored: false, steps: this.currentSteps };
    } catch (error) {
      console.error('서버 복원 실패:', error);
      return { restored: false, error: error.message };
    }
  }

  async getTodayStepsFromServer() {
    const url = "/health/steps/daily";
    const jToken = localStorage.getItem('jToken');
    const loginUser = sessionStorage.getItem("loginUser");

    if (!jToken || !loginUser) {
      return 0;
    }

    const user = JSON.parse(loginUser);
    const reqOptions = {
      headers: { Authorization: `Bearer ${jToken}` }
    };
    const reqBody = { usrInfoId: user?.usrInfoId };

    const today = new Date();
    const todayString = today.getFullYear() +
      String(today.getMonth() + 1).padStart(2, '0') +
      String(today.getDate()).padStart(2, '0');

    try {
      const res = await axiosApi.post(url, reqBody, reqOptions);
      if (res.data.resultCode === CODE.RCV_SUCCESS) {
        const healthInfo = res.data.result.healthWalkInfo;
        if (healthInfo && healthInfo.recDe === todayString) {
          return healthInfo.steps || 0;
        }
        return 0;
      }
      return 0;
    } catch (err) {
      console.error("서버 걸음수 조회 오류:", err);
      return 0;
    }
  }

  async syncWithServer(deviceSteps = 0) {
    try {
      const serverSteps = await this.getTodayStepsFromServer();
      const finalSteps = Math.max(deviceSteps, serverSteps);

      if (deviceSteps > serverSteps) {
        await this.sendToServer(deviceSteps, 'SYNC_UPDATE');
      }

      this.currentSteps = finalSteps;
      this.notifyListeners({ success: true, steps: finalSteps, triggerType: 'SYNC' });

      return { success: true, steps: finalSteps, serverSteps, deviceSteps };
    } catch (error) {
      console.error('동기화 실패:', error);
      return { success: false, error: error.message };
    }
  }

  async sendToServer(steps, triggerType) {
    const url = "/health/steps";
    const jToken = localStorage.getItem('jToken');
    const loginUser = sessionStorage.getItem("loginUser");

    if (!jToken || !loginUser) {
      console.log('로그인 정보 없음 - 서버 전송 건너뜀');
      return;
    }

    const user = JSON.parse(loginUser);

    const today = new Date();
    const recDe = today.getFullYear() +
      String(today.getMonth() + 1).padStart(2, '0') +
      String(today.getDate()).padStart(2, '0');

    const reqOptions = {
      headers: {
        Authorization: `Bearer ${jToken}`,
        'Content-Type': 'application/json'
      }
    };

    const reqBody = {
      usrInfoId: user?.usrInfoId,
      recDe: recDe,
      steps: Math.round(steps || 0),
      cnsumCalor: Math.round((steps || 0) * 0.04),
      walkDstnc: ((steps || 0) * 0.0008).toFixed(3)
    };

    try {
      const res = await axiosApi.post(url, reqBody, reqOptions);
      if (res.data.resultCode === CODE.RCV_SUCCESS) {
        console.log(`✅ 서버 전송 성공: ${steps}걸음 (${triggerType})`);
      } else {
        throw new Error(res.data.resultMessage);
      }
    } catch (err) {
      console.error("걸음수 서버 전송 오류:", err);
      throw err;
    }
  }

  resetPermission() {
    this.permissionChecked = false;
    this.hasPermission = false;
    try {
      const key = this.getUserStorageKey(this.STORAGE_KEY);
      localStorage.removeItem(key);
    } catch (error) {
      console.error('저장소 삭제 실패:', error);
    }
  }

  setPermissionGranted() {
    this.permissionChecked = true;
    this.hasPermission = true;
    this.savePermissionToStorage(true);
  }

  addListener(callback) {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  notifyListeners(data) {
    this.listeners.forEach(callback => {
      try { callback(data); } catch (e) { }
    });
  }

  async onEnterScreen(screenName) {
    console.log(`📱 ${screenName} 화면 진입`);

    // Android면 먼저 센서값 요청
    if (this.isAndroid && this.hasPermission) {
      this.requestAndroidSteps();
    }

    return this.updateSteps(`SCREEN_${screenName}`);
  }

  startBackground(minutes = 5) {
    this.stopBackground();
    this.interval = setInterval(() => {
      this.updateSteps('BACKGROUND');
    }, minutes * 60 * 1000);
  }

  stopBackground() {
    if (this.interval) {
      clearInterval(this.interval);
      this.interval = null;
    }
  }

  getStatus() {
    return {
      isUpdating: this.isUpdating,
      currentSteps: this.currentSteps,
      calculatedSteps: this.getCalculatedSteps(),
      backgroundActive: !!this.interval,
      hasPermission: this.hasPermission,
      permissionChecked: this.permissionChecked,
      isBaseStepsSet: this.isBaseStepsSet,
      androidSteps: this.androidSteps,
      deviceBaseSteps: this.deviceBaseSteps,
      serverBaseSteps: this.serverBaseSteps
    };
  }
}

const globalStepUpdater = new GlobalStepUpdater();

export const stepUpdater = {
  refresh: () => globalStepUpdater.updateSteps('MANUAL'),
  onEnterScreen: (name) => globalStepUpdater.onEnterScreen(name),
  startBackground: (min) => globalStepUpdater.startBackground(min),
  stopBackground: () => globalStepUpdater.stopBackground(),
  addListener: (cb) => globalStepUpdater.addListener(cb),
  getStatus: () => globalStepUpdater.getStatus(),
  resetPermission: () => globalStepUpdater.resetPermission(),
  setPermissionGranted: () => globalStepUpdater.setPermissionGranted(),
  getTodayStepsFromServer: () => globalStepUpdater.getTodayStepsFromServer(),
  syncWithServer: (deviceSteps) => globalStepUpdater.syncWithServer(deviceSteps),
  restoreFromServerOnFreshInstall: () => globalStepUpdater.restoreFromServerOnFreshInstall(),
  sendToServer: (steps, triggerType = 'MANUAL') => globalStepUpdater.sendToServer(steps, triggerType),
  checkDateChange: () => globalStepUpdater.checkDateChange(),
  resetDailyData: () => globalStepUpdater.resetDailyData(),
  getCalculatedSteps: () => globalStepUpdater.getCalculatedSteps()
};

export const useStepUpdater = () => {
  const [status, setStatus] = React.useState(globalStepUpdater.getStatus());

  React.useEffect(() => {
    const remove = globalStepUpdater.addListener(() => {
      setStatus(globalStepUpdater.getStatus());
    });
    return remove;
  }, []);

  return {
    ...status,
    refresh: stepUpdater.refresh,
    resetPermission: stepUpdater.resetPermission
  };
};

export default globalStepUpdater;